/**
 * Replay schema — the canonical serialized form of a match.
 *
 * The replay IS the canonical input-ordering: `inputs` MUST be sorted by
 * `(t asc, seat asc, seq asc)`. The refinement below enforces it at parse
 * time; nothing downstream needs to re-sort.
 *
 * A minimal replay carries zero inputs (`inputs: []`) — the sim spawns +
 * ends immediately, which is a legal (if boring) match.
 */

import { z } from "zod";
import {
  CONTENT_HASH_SCHEMA,
  MATCH_ID_SCHEMA,
  SEAT_INDEX_SCHEMA,
  SEQ_SCHEMA,
  TEAM_INDEX_SCHEMA,
  TICK_SCHEMA,
} from "./branded-schemas.js";
import { RESOLVED_LOADOUT_SCHEMA, SIM_INPUT_SCHEMA } from "./messages.js";
import { SIM_VERSION_PATTERN } from "./limits.js";

export const REPLAY_INPUT_SCHEMA = z.strictObject({
  t: TICK_SCHEMA,
  seat: SEAT_INDEX_SCHEMA,
  seq: SEQ_SCHEMA,
  input: SIM_INPUT_SCHEMA,
});

/**
 * Max length of `REPLAY_PLAYER_SCHEMA.botKind` — deliberately the SAME bound
 * the wire already puts on a bot identifier (`createMatch`'s
 * `manifestId: z.string().min(1).max(80)` in `messages.ts`), because that is
 * exactly the value the server writes here. Keeping the two in sync means a
 * `manifestId` the wire accepts can never be a `botKind` the replay writer
 * rejects (which would lose the whole replay — `ReplayWriter.writeTo`'s
 * `strictValidate` throws on a self-check failure).
 */
const REPLAY_BOT_KIND_MAX_LEN = 80;

export const REPLAY_PLAYER_SCHEMA = z
  .strictObject({
    seat: SEAT_INDEX_SCHEMA,
    team: TEAM_INDEX_SCHEMA,
    /** Display name. Assigned by server (default: `"Player <seat+1>"`). */
    name: z.string().min(1).max(64),
    /** Class/champion id. Content-validated by @pit/content on load. */
    class: z.string().min(1).max(64),
    /**
     * #323: this player's RESOLVED ability loadout — the server always writes
     * the concrete post-`resolveLoadout` ability-id list here (never the raw
     * `null` "use the default pool" shorthand a client may have sent), so a
     * replay is reproducible from its own bytes without re-running content
     * resolution. #540: uses `RESOLVED_LOADOUT_SCHEMA`, NOT the wire
     * `LOADOUT_SCHEMA` — this field holds the RESOLVED list, which for a
     * null-loadout request is the champion's FULL pool and can exceed
     * `MAX_LOADOUT_SIZE` (Afflictor/Warrior, both 13-ability pools); a replay
     * tool feeding this into `MatchConfig.seats[].loadout` still accepts
     * `null` defensively, but the writer never emits it.
     */
    loadout: RESOLVED_LOADOUT_SCHEMA,
    /**
     * ISSUE-791: seat PROVENANCE — was this seat driven by a human or by a bot?
     * Denormalised into the replay for the same reason `class` and the resolved
     * `loadout` above are: a replay must be interpretable from its own bytes,
     * with no join back to the server's database. `match_participant.is_bot`
     * (see `@pit/data`) is the same fact, and stays the authoritative record;
     * this is its anonymous, file-local copy.
     *
     * OPTIONAL, and deliberately NOT `.default(false)` — the two reasons are
     * independent and both binding:
     *   1. Compatibility: this is a `strictObject`, so replays written before
     *      the field existed would fail to parse if it were required.
     *   2. Semantics: ABSENT MEANS UNKNOWN, NOT HUMAN. Defaulting to `false`
     *      would relabel every seat of every pre-ISSUE-791 replay "human",
     *      which is precisely how a behavioural-cloning corpus ends up
     *      training a policy to imitate the existing bots. Consumers must
     *      keep the three-state distinction (see `@pit/replay`'s `seatKinds`).
     * The server writer (`apps/server/src/replayWriter.ts`) ALWAYS emits this
     * field and `botKind` below; the optionality exists solely for READING
     * pre-ISSUE-791 files.
     *
     * This pair is also the extension point for any further per-seat
     * provenance (which policy version drove the seat, whether a disconnected
     * human was bot-substituted mid-match): add a sibling optional field here
     * rather than inventing a second per-seat container.
     */
    isBot: z.boolean().optional(),
    /**
     * ISSUE-791: WHICH bot drove this seat, when `isBot` is true — the server's
     * own free-text bot identifier: a pinned `bots/manifest.json` entry id / RL
     * checkpoint reference (e.g. `20260724t011745z_mage_9ba3b30`) or the
     * `"dummy"` sentinel. Byte-identical to what `match_participant.bot_kind`
     * stores.
     *
     * `null` for a human seat, and for a bot seat whose kind isn't known.
     * NEVER an account id: a replay on disk deliberately carries only the
     * ANONYMOUS kind label, so it does not become personally-linked data
     * (ISSUE-791 §3 — identity resolution stays a DB join).
     */
    botKind: z.union([z.string().min(1).max(REPLAY_BOT_KIND_MAX_LEN), z.null()]).optional(),
  })
  .refine((p) => p.isBot === true || p.botKind === null || p.botKind === undefined, {
    // A non-null `botKind` on a non-bot seat is nonsense state; reject it at
    // the boundary rather than leave every consumer to decide which field wins.
    message: "botKind must be null/absent unless isBot is true",
    path: ["botKind"],
  });

export const REPLAY_MATCH_CONFIG_SCHEMA = z.strictObject({
  teamSize: z.number().int().gte(1).lte(16),
  rounds: z.number().int().gte(1).lte(64),
  /** Map id. Cross-validated by @pit/content on load. */
  map: z.string().min(1).max(64),
  /** Optional match id — replays can be produced without a live match. */
  matchId: z.union([MATCH_ID_SCHEMA, z.null()]),
});

/**
 * Assert `inputs` is sorted lexicographically by `(t, seat, seq)`. The
 * refinement is a boundary invariant; sims never re-sort at load.
 */
function inputsSorted(
  inputs: readonly {
    readonly t: number;
    readonly seat: number;
    readonly seq: number;
  }[],
): boolean {
  for (let i = 1; i < inputs.length; i++) {
    const a = inputs[i - 1];
    const b = inputs[i];
    // Defensive: `noUncheckedIndexedAccess` demands the guard even though
    // the loop bounds guarantee both are defined.
    if (a === undefined || b === undefined) return false;
    if (a.t < b.t) continue;
    if (a.t > b.t) return false;
    if (a.seat < b.seat) continue;
    if (a.seat > b.seat) return false;
    if (a.seq < b.seq) continue;
    // a.seq >= b.seq at same (t, seat) — must be strictly less
    return false;
  }
  return true;
}

export const REPLAY_SCHEMA = z
  .strictObject({
    simVersion: z.string().regex(SIM_VERSION_PATTERN, "simVersion must be X.Y.Z"),
    contentHash: CONTENT_HASH_SCHEMA,
    matchConfig: REPLAY_MATCH_CONFIG_SCHEMA,
    players: z.array(REPLAY_PLAYER_SCHEMA).min(1),
    inputs: z.array(REPLAY_INPUT_SCHEMA),
  })
  .refine((r) => inputsSorted(r.inputs), {
    message: "inputs must be sorted ascending by (t, seat, seq)",
    path: ["inputs"],
  });

export type Replay = z.infer<typeof REPLAY_SCHEMA>;
